import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { BigScreenDashboard } from '../models/big-screen-dashboard.model';
import {
  buildDashboardExport,
  parseDashboardExport,
  prepareImportedDashboards,
  slugify,
} from './dashboard-transfer';

/**
 * Persists user-defined big-screen dashboards to localStorage. This is the single
 * source of truth for the dashboards until a backend API exists; every read and
 * write goes through here so the storage key and JSON shape live in one place.
 */
@Injectable({
  providedIn: 'root',
})
export class BigScreenDashboardService {
  /** localStorage key holding the JSON array of dashboards. */
  private readonly storageKey = 'attendanceBigScreenDashboards';

  /**
   * All saved dashboards, newest-updated first. Malformed or absent storage
   * yields an empty list (logged) rather than throwing, so a corrupt entry can't
   * break the page.
   */
  getAll(): BigScreenDashboard[] {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        console.warn('Big-screen dashboards store is not an array; ignoring it.');
        return [];
      }
      return parsed as BigScreenDashboard[];
    } catch (error) {
      console.error('Failed to parse big-screen dashboards from storage:', error);
      return [];
    }
  }

  /**
   * The dashboards belonging to a single venue. A dashboard created for one venue
   * must not appear on another, so the picker reads through here rather than
   * {@link getAll}. A blank key yields an empty list.
   */
  getByVenue(venueKey: string): BigScreenDashboard[] {
    if (!venueKey) return [];
    return this.getAll().filter((dashboard) => dashboard.venueKey === venueKey);
  }

  getById(id: string): BigScreenDashboard | null {
    return this.getAll().find((dashboard) => dashboard.id === id) ?? null;
  }

  /**
   * Creates or updates a dashboard by `id`, then persists the whole list. Returns
   * the saved dashboard so callers can open it immediately.
   */
  save(dashboard: BigScreenDashboard): BigScreenDashboard {
    const dashboards = this.getAll();
    const index = dashboards.findIndex((existing) => existing.id === dashboard.id);
    if (index === -1) {
      dashboards.push(dashboard);
    } else {
      dashboards[index] = dashboard;
    }
    this.persist(dashboards);
    return dashboard;
  }

  delete(id: string): void {
    const remaining = this.getAll().filter((dashboard) => dashboard.id !== id);
    this.persist(remaining);
  }

  /** Fresh unique id for a new dashboard or widget. */
  createId(): string {
    return crypto.randomUUID();
  }

  // ---------------------------------------------------------------------------
  // Import / export
  // ---------------------------------------------------------------------------

  /**
   * Triggers a browser download of the given dashboards as a versioned JSON export
   * file, scoped to a venue. `filenameHint` seeds the download name (e.g. the
   * dashboard's name, or the venue for a bulk export).
   */
  export(
    dashboards: BigScreenDashboard[],
    venueKey: string,
    venueName: string,
    filenameHint: string
  ): void {
    const now = DateTime.now().toISO() ?? '';
    const envelope = buildDashboardExport(dashboards, venueKey, venueName, now);
    const json = JSON.stringify(envelope, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = `${slugify(filenameHint)}-dashboards.json`;
      link.click();
    } finally {
      // Release the object URL once the click has been dispatched.
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Validates raw JSON and returns the importable dashboards re-keyed onto the
   * target venue with fresh ids — but does not save them. Callers that want the
   * standard "add as new dashboards" behaviour use {@link import}; callers adopting
   * a single layout into an existing dashboard use this and handle persistence
   * themselves. Rethrows the transfer layer's typed errors (venue mismatch,
   * malformed file) for the caller to surface.
   */
  readImport(raw: string, venueKey: string, venueName: string): BigScreenDashboard[] {
    const parsed = parseDashboardExport(raw, venueKey, venueName);
    const now = DateTime.now().toISO() ?? '';
    return prepareImportedDashboards(parsed, venueKey, () => this.createId(), now);
  }

  /**
   * Validates and imports dashboards from raw JSON into the target venue, saving
   * each with a fresh id so an import never overwrites existing dashboards. Returns
   * the newly-saved dashboards. A storage failure surfaces the same quota message
   * as {@link save}.
   */
  import(raw: string, venueKey: string, venueName: string): BigScreenDashboard[] {
    const prepared = this.readImport(raw, venueKey, venueName);
    for (const dashboard of prepared) {
      this.save(dashboard);
    }
    return prepared;
  }

  private persist(dashboards: BigScreenDashboard[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(dashboards));
    } catch (error) {
      // Quota exceeded or storage disabled — surface it rather than failing silently.
      console.error('Failed to save big-screen dashboards to storage:', error);
      throw new Error('Could not save the dashboard. Your browser storage may be full.');
    }
  }
}
